import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { CustomTooltip } from '@components/CustomTooltip'
import TruncateLines from '@components/TruncateLines'
import DefaultCompany from '@components/icons/DefaultCompany'
import { Box, Typography } from '@mui/material'
import { removeHtmlTags } from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import chatApi from '../api/index'
import { Chat, ChatType, SortChatType } from '../types'
import ChatAvatar from './ChatAvatar'
import ChatDateTime from './ChatDateTime'
import ChatOptionsMenu from './ChatOptionsMenu'
import {
  ChatAvatarWrapper,
  ChatPreviewAvatarContainer,
  ChatPreviewContentContainer,
  DefaultCompanyLogoBadgeWrapper,
  UnreadCountBadge,
} from './index.styles'

interface ChatItemPreviewProps {
  chat: Chat
  onClick?: () => void
  unreadCount?: number
  showEmptyUnreadCount?: boolean
  onMenuClick: (option: string) => void
}

const ChatItemPreview = (props: ChatItemPreviewProps) => {
  const { chat, onClick, unreadCount, showEmptyUnreadCount, onMenuClick } = props

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: markAsUnreadMutation } = useMutation({
    mutationFn: chatApi.markChatAsUnread.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [chatApi.getUnreadMessagesCount.key] })
      queryClient.invalidateQueries({ queryKey: [chatApi.getChats.key, SortChatType.unread] })
      queryClient.invalidateQueries({ queryKey: [chatApi.getChatMessages.key, chat.id] })
    },
  })

  const handleMarkAsUnread = () => {
    onMenuClick('Mark as unread')

    markAsUnreadMutation({ chatId: chat.id })
  }

  const handleGoToProfile = () => {
    if (chat.otherParticipant) {
      onMenuClick('Go to profile')
      navigate(`/plus/profile/${chat.otherParticipant.id}/${chat.otherParticipant.type}`)
    }
  }

  const unreadCountLabel = useMemo(() => {
    if (showEmptyUnreadCount) {
      return ' '
    }

    if (unreadCount) {
      return unreadCount > 99 ? '99+' : unreadCount
    }

    return undefined
  }, [unreadCount, showEmptyUnreadCount])

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      paddingBottom="16px"
      borderBottom={`1px solid ${colors.lightGrey}`}
    >
      <ChatPreviewAvatarContainer
        flex="0 0 auto"
        onClick={onClick}
        $isDisabledStyle={!!chat.otherParticipant?.isAllDelegatesHidden || !!chat.otherParticipant?.isHidden}
      >
        {chat.isIncoming && chat.type === ChatType.GROUP && (
          <CustomTooltip title="Group conversation" placement="top" arrow={false} padding="5">
            <div>
              <DefaultCompanyLogoBadgeWrapper>
                {!chat.companyAvatar ? (
                  <DefaultCompany color="primary" fontSize="inherit" />
                ) : (
                  <ChatAvatarWrapper width="20px" height="20px" sx={{ marginRight: '2px' }}>
                    <img style={{ width: 'inherit' }} src={chat.companyAvatar} alt="chat-group-conversation-avatar" />
                  </ChatAvatarWrapper>
                )}
              </DefaultCompanyLogoBadgeWrapper>
            </div>
          </CustomTooltip>
        )}
        <ChatAvatar chat={chat} />
      </ChatPreviewAvatarContainer>

      <ChatPreviewContentContainer
        display="flex"
        flexDirection="column"
        flex="1 1 auto"
        maxWidth="240px"
        onClick={onClick}
        $isDisabledStyle={!!chat.otherParticipant?.isAllDelegatesHidden || !!chat.otherParticipant?.isHidden}
      >
        <Typography variant="caption">
          <TruncateLines lines={1}>{chat.name}</TruncateLines>
        </Typography>
        {!!chat.companyName && (
          <Typography variant="caption">
            <TruncateLines lines={1}>{chat.companyName}</TruncateLines>
          </Typography>
        )}
        {!!chat.subTitle && (
          <Typography variant="body1">
            <TruncateLines lines={1}>{chat.subTitle}</TruncateLines>
          </Typography>
        )}
        {!!chat.lastMessage && (
          <Typography variant="body1">
            <TruncateLines lines={2}>{removeHtmlTags(chat.lastMessage.content)}</TruncateLines>
          </Typography>
        )}
      </ChatPreviewContentContainer>

      <Box display="flex" flexDirection="column" justifyContent="space-between" flex="0 0 auto" alignItems="center">
        <ChatDateTime date={chat.date} />
        {unreadCountLabel ? (
          <Box mt="3px">
            <UnreadCountBadge>{unreadCountLabel}</UnreadCountBadge>
          </Box>
        ) : null}
        {chat?.otherParticipant && <ChatOptionsMenu onMarkAsUnread={handleMarkAsUnread} onGoToProfile={handleGoToProfile} />}
      </Box>
    </Box>
  )
}

export default ChatItemPreview
